/**
 * Reminder target vocabulary — UI-only, which is why it lives in the frontend rather than
 * in `@snootsvet/shared`. The server accepts any non-empty string for `target` (see
 * `treatmentReminderTargetSchema`), because `product-name` regions (UK) use the vaccine's
 * own product name; this disease list is what `disease-list` regions (US) offer in the
 * picker. Nothing in the backend reads it.
 *
 * The `target` string is the auto-supersede match key, so these values are stable.
 */

export const DOG_VACCINE_TARGETS = [
  'Rabies',
  'DHPP',
  'Leptospirosis',
  'Bordetella',
  'Canine Influenza',
  'Lyme',
] as const

export const CAT_VACCINE_TARGETS = [
  'Rabies',
  'FVRCP',
  'Feline Leukemia (FeLV)',
  'Bordetella',
] as const

/** The union of every target across species (deduped, dog order first). */
export const TREATMENT_REMINDER_TARGETS = [
  ...DOG_VACCINE_TARGETS,
  ...CAT_VACCINE_TARGETS.filter(
    (t) => !DOG_VACCINE_TARGETS.includes(t as (typeof DOG_VACCINE_TARGETS)[number])
  ),
] as const

/**
 * A reminder target. Free text: `disease-list` regions (US) constrain it to
 * {@link TREATMENT_REMINDER_TARGETS} in the UI, while `product-name` regions (UK) use the
 * vaccine's product name. Kept as a plain string so both models share one column/type.
 */
export type TreatmentReminderTarget = string

/**
 * Returns the reminder targets to offer for a patient's species. Normalizes the
 * free-text species (trim + lowercase); unrecognized species get the full union
 * so options are never hidden for exotics.
 */
export function vaccineTargetsForSpecies(species: string): TreatmentReminderTarget[] {
  const normalized = species.trim().toLowerCase()
  if (normalized === 'dog') return [...DOG_VACCINE_TARGETS]
  if (normalized === 'cat') return [...CAT_VACCINE_TARGETS]
  return [...TREATMENT_REMINDER_TARGETS]
}

/**
 * Brand/abbreviation shorthands that appear in product names but not in a target's
 * canonical label — so name-matching can still resolve e.g. "Nobivac Lepto", a "…KC"
 * kennel-cough vaccine, or a "…FeLV" product. A target's own (lowercased) name is
 * always a match candidate too, so exact names (Rabies, FVRCP, Lyme) need no entry.
 */
const VACCINE_TARGET_NAME_ALIASES: Partial<Record<TreatmentReminderTarget, readonly string[]>> = {
  DHPP: ['dhp', 'dapp'],
  Leptospirosis: ['lepto'],
  Bordetella: ['kennel cough', 'kc'],
  'Canine Influenza': ['influenza', 'civ'],
  'Feline Leukemia (FeLV)': ['feline leukemia', 'felv', 'leukemia'],
}

/**
 * Finds the reminder target a product name refers to, for pre-selecting the disease
 * when adding a booster reminder. Case-insensitive; matches a target's canonical name
 * or a known alias as a substring of the product name, returning the first match in the
 * given target order (or null). Best-effort convenience — the vet can always change it —
 * so it favours a confident single match over exhaustively covering every brand name.
 */
export function findMatchingVaccineTarget(
  productName: string,
  targets: readonly TreatmentReminderTarget[]
): TreatmentReminderTarget | null {
  const name = productName.toLowerCase()
  for (const target of targets) {
    const candidates = [target.toLowerCase(), ...(VACCINE_TARGET_NAME_ALIASES[target] ?? [])]
    if (candidates.some((c) => name.includes(c))) return target
  }
  return null
}
